import { useState } from "react";
import { ThumbsUp, PenLine, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Thread, Composer, StatusPill } from "@/components/feedback/Thread";
import { approvalText } from "@/lib/feedback";

export const NameDialog = ({ onSave, onCancel }) => {
  const [name, setName] = useState("");
  const save = (e) => { e.preventDefault(); if (name.trim()) onSave(name.trim()); };
  return (
    <Dialog open onOpenChange={(o) => !o && onCancel()}>
      <DialogContent className="max-w-sm bg-[#111827] border-slate-700 text-slate-100" data-testid="client-name-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">What's your name?</DialogTitle>
          <DialogDescription className="text-slate-400">Shown next to your approvals and comments. We'll remember it on this device.</DialogDescription>
        </DialogHeader>
        <form onSubmit={save} className="space-y-3">
          <input autoFocus data-testid="client-name-input" value={name} onChange={(e) => setName(e.target.value)} maxLength={60} placeholder="e.g. Rahul Rao"
            className="w-full h-11 rounded-md bg-[#0B0F19] border border-slate-700 focus:border-cyan-500 outline-none px-3 text-sm" />
          <button type="submit" data-testid="client-name-save" disabled={!name.trim()} className="w-full h-10 rounded-md bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-semibold text-sm disabled:opacity-40 transition-colors">Continue</button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export const ClientPanel = ({ s, entries, onPost, name, onChangeName }) => {
  const [changes, setChanges] = useState(false);
  const [busy, setBusy] = useState(false);
  const approve = async () => { setBusy(true); try { await onPost("approve", ""); } finally { setBusy(false); } };
  const approved = s?.status === "approved";
  return (
    <section className="space-y-3 border-t border-slate-800 pt-4" data-testid="client-feedback">
      <div className="flex items-center gap-2">
        <p className="font-mono text-[10px] uppercase tracking-wider text-cyan-400">Your review</p>
        <StatusPill s={s} testid="client-item-status" />
      </div>
      {s?.status && <p className="text-[11px] text-slate-400 -mt-1" data-testid="client-item-status-text">{approvalText(s)}</p>}
      <div className="grid grid-cols-2 gap-2">
        <button data-testid="client-approve-btn" onClick={approve} disabled={approved || busy}
          className="h-10 rounded-md bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold text-sm flex items-center justify-center gap-1.5 transition-colors disabled:opacity-50">
          {busy ? <Loader2 size={15} className="animate-spin" /> : <ThumbsUp size={15} />}{approved ? "Approved" : "Approve"}
        </button>
        <button data-testid="client-request-changes-btn" onClick={() => setChanges(!changes)}
          className={`h-10 rounded-md border text-sm font-semibold flex items-center justify-center gap-1.5 transition-colors ${changes ? "border-amber-400 bg-amber-500/10 text-amber-200" : "border-amber-500/50 text-amber-300 hover:bg-amber-500/10"}`}>
          <PenLine size={15} />Request changes
        </button>
      </div>
      {changes && <Composer testid="client-changes" tone="amber" autoFocus placeholder="What should we change on this item?" cta="Send change request"
        onSend={async (t) => { const r = await onPost("changes", t); if (r !== false) setChanges(false); return r; }} />}
      <Thread entries={entries} empty="No comments on this item yet." />
      <Composer testid="client-comment" placeholder="Add a comment or question…" cta="Post comment" onSend={(t) => onPost("comment", t)} />
      {name && <p className="text-[11px] text-slate-500" data-testid="client-signed-as">Commenting as <span className="text-slate-300">{name}</span> · <button onClick={onChangeName} className="text-cyan-400 hover:underline" data-testid="client-change-name">change</button></p>}
    </section>
  );
};
